import { Avatar, Box, Fab, Skeleton, styled, TextField, Typography } from '@mui/material'
import { useFormik } from 'formik'
import _, { isEmpty, omit, omitBy, some } from 'lodash'
import React, { FC } from 'react'
import { FileUploader } from 'react-drag-drop-files'
import { object, string } from 'yup'
import { Notistack } from '../../../../components/Notistack'
import { useUserStore } from '../../../../store/storeUser'
import { useWrapperStore } from '../../../../store/storeWrapper'
import { nameValidator } from '../../../../validators/nameValidator'
import PenIcon from './../../../../assets/icons/PenIcon.svg?react'
import { ActionButton } from './ActionButton'
import { Block } from './Block'
import { FieldsWrapper } from './FieldsWrapper'

const FormWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'end',
    gap: '24px',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'center',
    },
}))

const validationSchema = object()
    .shape({
        profile_pic: string(),
        firstname: nameValidator,
        lastname: nameValidator,
    })
    .test('profile-data', 'Хотя бы одно поле должно быть заполнено', (values) =>
        some(values, Boolean),
    )

export const UserPersonalData: FC = () => {
    const { userPersonalData, updateUserPersonalData, error } = useUserStore()
    const [openSnackbar, setOpenSnackbar] = React.useState(false)
    const { uploadFile, isLoadingUploadFiles } = useWrapperStore((state) => state)
    const {
        values,
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        setValues,
        touched,
        dirty,
        isValid,
    } = useFormik({
        initialValues: {
            ...omit(
                _.cloneDeepWith(userPersonalData, (val) => (val === null ? '' : undefined)),
                ['email'],
            ),
        },
        enableReinitialize: true,
        validateOnBlur: true,
        validationSchema,
        onSubmit: async (values) => {
            await updateUserPersonalData(omitBy(values, isEmpty))

            setOpenSnackbar((prev) => !prev)
        },
    })

    const handleChangePicture = async (file: File) => {
        const profile_pic = await uploadFile(file)

        setValues({ profile_pic })
    }

    const profileAvatar = isLoadingUploadFiles ? (
        <Skeleton variant="circular" width={112} height={112} />
    ) : (
        <Avatar
            sx={{
                width: '112px',
                height: '112px',
                borderRadius: '100px',
                backgroundColor: '#F4F4F6',
            }}
            src={values.profile_pic}
        />
    )

    return (
        <Block>
            <Typography
                variant="Header3"
                sx={{
                    color: '#1B1C1D',
                }}>
                Личная информация
            </Typography>
            <form onSubmit={handleSubmit} style={{ marginTop: '44px' }}>
                <FormWrapper>
                    <Box
                        sx={{
                            width: '112px',
                            height: '112px',
                            borderRadius: '100px',
                            position: 'relative',
                            alignSelf: 'center',
                        }}>
                        {profileAvatar}
                        <FileUploader
                            types={['jpeg', 'png', 'webp', 'bmp', 'jpg']}
                            classes="upload-avatar"
                            style={{
                                '&:focus-within': { outline: 'none' },
                            }}
                            handleChange={handleChangePicture}
                            name="profile_pic"
                            id="profile_pic">
                            <Fab size="small" color="primary">
                                <PenIcon />
                            </Fab>
                        </FileUploader>
                    </Box>

                    <FieldsWrapper>
                        <TextField
                            fullWidth
                            id="user_firstname"
                            name="firstname"
                            value={values.firstname}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.firstname && Boolean(errors.firstname)}
                            helperText={touched.firstname && errors.firstname}
                            variant="outlined"
                            label="Имя"
                            placeholder="Введите имя"
                            margin="normal"
                            slotProps={{
                                htmlInput: {
                                    maxLength: 100,
                                },
                            }}
                        />
                        <TextField
                            fullWidth
                            id="user_lastname"
                            name="lastname"
                            value={values.lastname}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.lastname && Boolean(errors.lastname)}
                            helperText={touched.lastname && errors.lastname}
                            variant="outlined"
                            label="Фамилия"
                            placeholder="Введите фамилию"
                            margin="normal"
                            slotProps={{
                                htmlInput: {
                                    maxLength: 100,
                                },
                            }}
                        />
                    </FieldsWrapper>
                    <ActionButton type="submit" disabled={!(isValid && dirty)} variant="contained">
                        Сохранить
                    </ActionButton>
                </FormWrapper>
            </form>
            <Notistack
                open={openSnackbar}
                isError={error}
                message={error ?? 'Личная информация успешно сохранена'}
                onClose={() => setOpenSnackbar(false)}
            />
        </Block>
    )
}
